import Dexie, { type Table } from "dexie";

import type { DiscussionMessage, DiscussionThread, MentionableEntity } from "@/types";

export interface OfflineQueuedMessage {
  id: string;
  organizationId: number;
  threadId: number;
  body: string;
  mentionUserIds: number[];
  mentionGroupIds: number[];
  createdAt: string;
  sequence?: number;
}

interface ThreadListCache {
  organizationId: number;
  threads: DiscussionThread[];
  availableTags: string[];
  updatedAt: string;
}

interface ThreadMessagesCache {
  key: string;
  organizationId: number;
  threadId: number;
  thread: DiscussionThread;
  messages: DiscussionMessage[];
  mentionables: MentionableEntity[];
  updatedAt: string;
}

const DB_NAME = "condor-discussions";
const DB_VERSION = 3;

class DiscussionDB extends Dexie {
  thread_lists!: Table<ThreadListCache, number>;
  thread_messages!: Table<ThreadMessagesCache, string>;
  queued_messages!: Table<OfflineQueuedMessage, string>;

  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION)
      .stores({
        thread_lists: "organizationId",
        thread_messages: "key, organizationId, threadId",
        queued_messages: "id, organizationId, threadId",
      })
      .upgrade((tx) =>
        Promise.all([
          tx.table("thread_lists").clear(),
          tx.table("thread_messages").clear(),
          tx.table("queued_messages").clear(),
        ]),
      );
  }
}

let dbInstance: DiscussionDB | null = null;

const toPlainObject = <T>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T;

export const getDiscussionDB = () => {
  if (!dbInstance) {
    dbInstance = new DiscussionDB();
  }
  return dbInstance;
};

const messageKey = (organizationId: number, threadId: number) =>
  `${organizationId}:${threadId}`;

export const saveThreadList = async (
  organizationId: number,
  threads: DiscussionThread[],
  availableTags: string[],
) => {
  const db = getDiscussionDB();
  const payload: ThreadListCache = {
    organizationId,
    threads: toPlainObject(threads),
    availableTags: toPlainObject(availableTags),
    updatedAt: new Date().toISOString(),
  };
  await db.thread_lists.put(payload);
};

export const loadThreadList = async (organizationId: number) => {
  const db = getDiscussionDB();
  return db.thread_lists.get(organizationId);
};

export const saveThreadMessages = async (
  organizationId: number,
  threadId: number,
  thread: DiscussionThread,
  messages: DiscussionMessage[],
  mentionables: MentionableEntity[],
) => {
  const db = getDiscussionDB();
  const payload: ThreadMessagesCache = {
    key: messageKey(organizationId, threadId),
    organizationId,
    threadId,
    thread: toPlainObject(thread),
    messages: toPlainObject(messages),
    mentionables: toPlainObject(mentionables),
    updatedAt: new Date().toISOString(),
  };
  await db.thread_messages.put(payload);
};

export const loadThreadMessages = async (organizationId: number, threadId: number) => {
  const db = getDiscussionDB();
  return db.thread_messages.get(messageKey(organizationId, threadId));
};
